import {
  computed,
  inject,
  isRef,
  MaybeRef,
  onBeforeUnmount,
  onMounted,
  ref
} from 'vue'
import { useStore } from 'vuex'

import { ModalComponentName } from '@/components/Modal/types'
import {
  TrackEventsNames,
  TrackEventsPopupSubtypes,
  TrackEventsTypes
} from '@/constants'

import {
  BOTTOM_SPACING,
  DEFAULT_TOP_POSITION,
  MIN_MODAL_SPACE,
  SCROLL_OFFSET
} from './types/modal'

export function useModal(modalContainerValueOrRef?: MaybeRef<HTMLElement>) {
  const tce = inject('trackCustomEvent')

  const store = useStore()
  const modalContainer = ref<HTMLElement | null>(null)
  const top = ref<number>(DEFAULT_TOP_POSITION)

  if (isRef(modalContainerValueOrRef)) {
    modalContainer.value = modalContainerValueOrRef.value
  } else {
    modalContainer.value = modalContainerValueOrRef ?? null
  }

  const registrationTab = computed<boolean>(
    () => store.getters['modal/isRegistrationTab']
  )
  const options = computed<any>(() => store.getters['modal/getOptions'])

  const open = (name: ModalComponentName): void => {
    store.dispatch('modal/open', name)
  }

  const close = (
    name: ModalComponentName,
    subtype: TrackEventsPopupSubtypes = TrackEventsPopupSubtypes.CLOSED
  ): void => {
    switch (name) {
      case ModalComponentName.COURSES_VIEW_CHANGED:
        tce(TrackEventsNames.CATALOGS_TO_ROADMAP_POPUP, {
          subtype,
          type: TrackEventsTypes.ROADMAP
        })
        break
      case ModalComponentName.INSTALL_MOBILE_APP:
        tce(TrackEventsNames.POPUP, {
          subtype: TrackEventsPopupSubtypes.CLOSED,
          type: TrackEventsTypes.TO_APP
        })
        break
      case ModalComponentName.GO_TO_MOBILE_APP:
        tce(TrackEventsNames.POPUP, {
          subtype: TrackEventsPopupSubtypes.CLOSED,
          type: TrackEventsTypes.OPEN_APP
        })
    }

    store.dispatch('modal/close', name)
  }

  const scrollWindow = (): void => {
    if (!modalContainer.value) return

    const modalHeight = modalContainer.value.clientHeight ?? 0
    const { innerHeight, pageYOffset } = window
    const size = innerHeight - modalHeight

    if (size < MIN_MODAL_SPACE) {
      const y = -(pageYOffset - SCROLL_OFFSET)
      if (y >= size - BOTTOM_SPACING) {
        top.value = y
      } else {
        top.value = size - BOTTOM_SPACING
      }
    }
  }

  onMounted(() => {
    window.addEventListener('scroll', scrollWindow)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', scrollWindow)
  })

  return {
    top,
    open,
    close,
    scrollWindow,
    registrationTab,
    options
  }
}
